import { produce } from 'immer'
import type { FactTable, Metric, Dimension, Filter, RawSqlFactTablePayload } from 'AnalyticsDashboard/utils/factTableHelpers'

// Re-export so callers don't import from two places.
export type { FactTable, Metric, Dimension, Filter, RawSqlFactTablePayload }

// --- State types ---

export interface QueryState {
  chartType: string
  metrics: Metric[]
  dimensions: Array<{ id: string; timeBucket?: string }>
  primaryDimension?: { id: string; label?: string }
  secondaryDimension?: { id: string; label?: string }
  chartOptions?: Record<string, any>
  controlOptions?: { showChartType?: boolean; showTimeBucket?: boolean }
  supportedChartTypes?: string[]
  overrideFilters?: Record<string, any>
  orderBy?: Array<{ kind?: string; id: string; direction: string }>
  limit?: number | null
  comparePeriods?: number | null
  global?: boolean
  parameters?: Record<string, any>
  chartComponent?: any
  data: any
  dataLoading: boolean
  dataError: string | null
  firstFetch: boolean
  queryInfo?: any
  filterValues?: any
  filtersEnabled?: boolean
  // AI-generated raw SQL fact table backing this query. When present, the run
  // request POSTs it alongside metrics/dimensions/filter_values and the slot UI
  // sees a synthetic `raw_sql` fact table via rawSqlFactTableToFactTable.
  rawSqlFactTable?: RawSqlFactTablePayload
  // Cached fact_tables_info-shaped synthesis from the backend validate endpoint.
  // Populated lazily by AnalyticsQuery once the tile has been validated. Use
  // this when present — it includes the real segment dims (incl. dynamic
  // per-client organisation segments) the client-side mining can only
  // approximate. Not persisted to the saved tile config.
  rawSqlSynthesizedFactTable?: FactTable
  // ID of the AssistantConversation row backing this query's AI history.
  // Sent on every Regenerate so Claude sees the full prior conversation
  // (including its own tool_use blocks). Persisted on the saved dashboard tile.
  assistantConversationId?: number
}

export interface AnalyticsState {
  queriesInfo: FactTable[]
  queriesInfoLoaded: boolean
  queries: Record<string, QueryState>
  filterValues: Record<string, any>
  uiEnabled: boolean
  global: boolean
  local: Record<string, any>
  requiredFilters: string[]
  timeDimension?: any
  sharedFilters?: any
  sharedFiltersEnabled?: boolean
  sharedTimeDimension?: any
}

// --- Action types ---

type Action =
  | { type: 'REGISTER_QUERY'; payload: { id: string } & Partial<QueryState> }
  | { type: 'SET_QUERIES_INFO'; payload: FactTable[] }
  | { type: 'SET_QUERY_DATA'; payload: { id: string; data: any } }
  | { type: 'SET_QUERY_DATA_LOADING'; payload: { id: string; loading: boolean } }
  | { type: 'UPDATE_QUERY'; payload: { id: string; data: Partial<QueryState> } }
  | { type: 'UPDATE_DIMENSION'; payload: { id: string; dimensionId: string; data: Record<string, any> } }
  | { type: 'SET_FILTER'; payload: { id: string; value: any } }
  | { type: 'SET_TIME_DIMENSION'; payload: { value: any } }
  | { type: 'UNREGISTER_QUERY'; payload: { id: string } }
  | { type: 'UPSERT_LOCAL'; payload: Record<string, any> }

const analyticsReducer = produce((draft: AnalyticsState, action: Action) => {
  switch (action.type) {
    case 'REGISTER_QUERY': {
      const existing = draft.queries[action.payload.id]
      if (existing) {
        Object.assign(existing, action.payload)
      } else {
        draft.queries[action.payload.id] = {
          dataLoading: false,
          dataError: null,
          firstFetch: true,
          data: null,
          chartType: (action.payload as any).defaultChartType,
          metrics: [],
          dimensions: [],
          ...action.payload
        } as QueryState
      }
      break
    }

    case 'SET_QUERIES_INFO':
      draft.queriesInfo = action.payload
      draft.queriesInfoLoaded = true
      break

    case 'SET_QUERY_DATA':
      draft.queries[action.payload.id].data = action.payload.data
      draft.queries[action.payload.id].dataLoading = false
      break

    case 'SET_QUERY_DATA_LOADING':
      draft.queries[action.payload.id].dataLoading = action.payload.loading
      break

    case 'UPDATE_QUERY':
      draft.queries[action.payload.id] = { ...draft.queries[action.payload.id], ...action.payload.data }
      break

    case 'UPDATE_DIMENSION': {
      const { id: queryId, dimensionId, data } = action.payload
      const dimensionIndex = draft.queries[queryId].dimensions.findIndex(d => d.id === dimensionId)
      draft.queries[queryId].dimensions[dimensionIndex] = { ...draft.queries[queryId].dimensions[dimensionIndex], ...data }
      break
    }

    case 'SET_FILTER': {
      const { id, value } = action.payload
      draft.filterValues = { ...draft.filterValues, [id]: value }
      break
    }

    case 'SET_TIME_DIMENSION':
      draft.timeDimension = action.payload.value
      break

    case 'UNREGISTER_QUERY':
      delete draft.queries[action.payload.id]
      break

    case 'UPSERT_LOCAL':
      draft.local = { ...draft.local, ...action.payload }
      break
  }
})

export default analyticsReducer
